import type { FormSubmissionStatus } from '../../../utilities/form-status'
import { CurrencyPipe, DatePipe, I18nPluralPipe, NgOptimizedImage } from '@angular/common'
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnInit,
    Output,
    output,
    SimpleChanges,
    ViewChild,
    signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import {
    NgbDate,
    NgbDateNativeAdapter,
    NgbDatepickerI18n,
    NgbInputDatepicker,
    NgbDatepickerModule,
    NgbDatepickerNavigateEvent,
    NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap'
import {
    Observable,
    Subject,
    catchError,
    distinctUntilChanged,
    filter,
    forkJoin,
    map,
    of,
    switchMap,
    tap,
} from 'rxjs'
import { Availability } from '../../../domain/Availability'
import { BookingDetails } from '../../../domain/BookingDetails'
import { Event as VenueEvent } from '../../../domain/Event'
import { Venue } from '../../../domain/Venue'
import { VenueOpenDaysCalendar } from '../../../domain/VenueOpenDaysCalendar'
import { EnquiryFormComponent } from '../../../enquiry-form/enquiry-form.component'
import { CurrencyUnitPipe } from '../../../pipes/currency-unit.pipe'
import {
    EventRecurrenceDescriptionPipe,
} from '../../../pipes/event-recurrence-description.pipe'
import { GBTimeZoneDatepickerI18n } from '../../../services/gb-time-zone-datepicker-i18n.service'
import { isVenueNotInServiceError } from '../../../services/venue-not-in-service-error'
import { AvailabilityService } from '../../../services/availability.service'
import { richTextPlainText, truncateRichTextHtml } from '../../../utilities/rich-text'
import type {
    VenueStorefrontSearchChange,
    VenueStorefrontAvailabilityStatus,
    VenueStorefrontBookingSearch,
    VenueStorefrontBookingSearchInitialValues,
} from '../venue-storefront-booking-flow/venue-storefront-booking-flow.types'
import { environment } from '../../../../environments/environment'
import { QuillModule } from 'ngx-quill'

type AvailabilityRequest = {
    date: Date
    initialLoad: boolean
    generation: number
    venue: Venue
    restrictedReasonIds?: string[]
}

type AvailabilityResult = {
    request: AvailabilityRequest
    availability: Availability | null
    error: unknown | null
}

type ConfiguredBookingContext = {
    displayName: string
    event: VenueEvent | null
    imageUrl: string | null
    description: string | null
    hasCharges: boolean
}

@Component({
    selector: 'app-venue-storefront-booking-search',
    standalone: true,
    imports: [
        QuillModule,
        CurrencyUnitPipe,
        DatePipe,
        EnquiryFormComponent,
        EventRecurrenceDescriptionPipe,
        I18nPluralPipe,
        NgbDatepickerModule,
        NgOptimizedImage,
        ReactiveFormsModule,
    ],
    providers: [
        CurrencyPipe,
        {
            provide: NgbDatepickerI18n,
            useClass: GBTimeZoneDatepickerI18n,
        },
    ],
    templateUrl: './venue-storefront-booking-search.component.html',
    styleUrls: ['./venue-storefront-booking-search.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueStorefrontBookingSearchComponent implements AfterViewInit, OnInit, OnChanges {
    @Input({ required: true }) venue!: Venue
    @Input() initialValues: VenueStorefrontBookingSearchInitialValues | null = null
    @Input() largePartyEnquiry = false
    @Input() updatingBooking: BookingDetails | null = null
    availabilityStatusChange = output<VenueStorefrontAvailabilityStatus>()
    bookingContextResolved = output<void>()
    searchCriteriaChanged = output<VenueStorefrontSearchChange>()
    enquiryStatusChange = output<FormSubmissionStatus>()

    @Output() searchAvailability = new EventEmitter<VenueStorefrontBookingSearch>()
    @Output() largePartyEnquiryRequested = new EventEmitter<
        VenueStorefrontBookingSearchInitialValues
    >()
    @Output() storefrontReturnRequested = new EventEmitter<void>()
    @ViewChild('cardContent', { static: true }) private cardContent!: ElementRef<HTMLElement>
    @ViewChild(NgbInputDatepicker) private datepicker?: NgbInputDatepicker

    readonly form = new FormGroup({
        partySize: new FormControl<number | null>(null, [
            Validators.required,
            Validators.min(1),
        ]),
        date: new FormControl<NgbDateStruct | null>(null, Validators.required),
        idealTime: new FormControl<Date | null>(null, Validators.required),
        requiresWheelchairAccess: new FormControl(false, { nonNullable: true }),
        requiresDogFriendly: new FormControl(false, { nonNullable: true }),
        requiresPramAccessible: new FormControl(false, { nonNullable: true }),
        requiresHighchairFriendly: new FormControl(false, { nonNullable: true }),
    })
    protected readonly availability = signal<Availability | null>(null)
    protected readonly isLoading = signal(true)
    protected readonly errorMessage = signal<string | null>(null)
    protected readonly venueNotInService = signal(false)
    protected readonly contentHeight = signal<number | null>(null)
    protected readonly selectedBookingContext = signal<ConfiguredBookingContext | null>(null)
    protected readonly partySizePluralMapping = {
        '=1': '1 person',
        other: '# people',
    }
    protected readonly earliestDate: NgbDateStruct
    protected openDaysCalendar = new VenueOpenDaysCalendar()
    protected readonly isDateDisabled = (date: NgbDate) => {
        const event = this.selectedBookingContext()?.event
        if (event) {
            return !event.recurrence.appliesToDate(
                new Date(date.year, date.month - 1, date.day)
            )
        }
        return this.openDaysCalendar.isDateClosed(date.year, date.month, date.day)
    }

    private readonly availabilityRequests = new Subject<AvailabilityRequest>()
    private readonly destroyRef: DestroyRef
    private readonly ngbDateAdapter = new NgbDateNativeAdapter()
    private initialised = false
    private loadGeneration = 0
    private resolvedAvailabilityDate: Date | null = null
    private visibleDatepickerMonth: NgbDateStruct | null = null
    private contentResizeObserver?: ResizeObserver

    constructor(
        private availabilityService: AvailabilityService,
        private ngZone: NgZone,
        destroyRef: DestroyRef
    ) {
        this.destroyRef = destroyRef
        this.earliestDate = this.ngbDateAdapter.fromModel(new Date())!
        this.destroyRef.onDestroy(() => this.contentResizeObserver?.disconnect())
    }

    ngOnInit() {
        this.bindAvailabilityRequests()
        this.bindDateChanges()
        this.bindPartySizeChanges()
        this.initialised = true
        this.initialiseForVenue()
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.initialised && (
            changes['venue']
            || changes['initialValues']
            || changes['largePartyEnquiry']
            || changes['updatingBooking']
        )) {
            this.initialiseForVenue()
        }
    }

    ngAfterViewInit() {
        if (typeof ResizeObserver === 'undefined') {
            return
        }
        this.contentResizeObserver = new ResizeObserver(([entry]) => {
            if (!entry) {
                return
            }
            const height = entry.contentRect.height
            this.ngZone.run(() => {
                if (this.contentHeight() !== height) {
                    this.contentHeight.set(height)
                }
            })
        })
        this.contentResizeObserver.observe(this.cardContent.nativeElement)
    }

    protected get selectedDate(): Date | null {
        return this.ngbDateAdapter.toModel(this.form.controls.date.value)
    }

    protected possiblePartySizes(): number[] {
        const availability = this.availability()
        if (!availability) {
            return []
        }
        const sizes = availability.possiblePartySizes(
            this.selectedDate,
            this.initialValues?.restrictedReasonIds,
            this.initialValues?.eventId
        )
        const additionalSizes = [
            this.updatingBooking ? null : availability.largePartySizeThreshold(
                this.selectedDate,
                this.initialValues?.eventId
            ),
            this.form.controls.partySize.value,
        ].filter((size): size is number => size !== null && !sizes.includes(size))
        return [...sizes, ...additionalSizes]
            .filter((size, index, allSizes) => allSizes.indexOf(size) === index)
            .sort((first, second) => first - second)
    }

    protected partySizeEdited() {
        const partySize = this.form.controls.partySize.value
        if (partySize !== null) {
            this.searchCriteriaChanged.emit({ field: 'party_size', value: partySize })
        }
    }

    protected idealTimeEdited() {
        const time = this.form.controls.idealTime.value
        if (time) {
            this.searchCriteriaChanged.emit({ field: 'ideal_time', value: time.toISOString() })
        }
    }

    protected searchDateEdited(date: NgbDate) {
        const selectedDate = this.ngbDateAdapter.toModel(date)
        if (selectedDate) {
            this.searchCriteriaChanged.emit({ field: 'date', value: selectedDate.toISOString() })
        }
    }

    protected compareDates(first: Date | null, second: Date | null): boolean {
        return first?.getTime() === second?.getTime()
    }

    protected idealTimeOptions(availability: Availability): Date[] {
        const event = this.selectedBookingContext()?.event
        if (event) {
            return availability.bookingSlotTimesForEvent(event.id)
        }
        const times = availability.intervalIdealTimes
        const selectedTime = this.form.controls.idealTime.value
        if (times.length > 0 || selectedTime === null) {
            return times
        }
        return [selectedTime]
    }

    protected onDatepickerNavigate(event: NgbDatepickerNavigateEvent) {
        this.visibleDatepickerMonth = {
            year: event.next.year,
            month: event.next.month,
            day: 1,
        }
        if (!this.selectedBookingContext()?.event) {
            this.loadOpenDaysForMonth(new Date(event.next.year, event.next.month - 1, 1))
        }
    }

    protected retry() {
        this.availabilityStatusChange.emit('retry')
        this.requestAvailability(this.selectedDate ?? new Date(), false)
    }

    protected selectedLargePartyAvailability(): Availability | null {
        const availability = this.availability()
        return this.selectedPartyIsLarge(availability) ? availability : null
    }

    protected cardView(): 'search' | 'large-party-enquiry' | 'large-party-message' {
        if (this.largePartyEnquiryVenue() !== null) {
            return 'large-party-enquiry'
        }
        return this.selectedUnavailableLargePartyAvailability() === null
            ? 'search'
            : 'large-party-message'
    }

    protected largePartyEnquiryVenue(): Venue | null {
        return this.largePartyEnquiry ? this.venue : null
    }

    protected selectedUnavailableLargePartyAvailability(): Availability | null {
        const availability = this.selectedLargePartyAvailability()
        return availability !== null && !availability.venue.enquiriesEnabled
            ? availability
            : null
    }

    protected returnToSearch(availability: Availability) {
        this.form.controls.partySize.setValue(availability.defaultReservablePartySize())
    }

    protected submit() {
        const availability = this.availability()
        const partySize = this.form.controls.partySize.value
        const date = this.selectedDate
        const idealTime = this.form.controls.idealTime.value
        if (
            this.form.invalid
            || !availability
            || !partySize
            || !date
            || !idealTime
            || this.isLoading()
        ) {
            this.form.markAllAsTouched()
            return
        }

        if (this.selectedPartyIsLarge(availability)) {
            this.requestLargePartyEnquiryIfNeeded(availability, true)
            return
        }

        const selectedIdealTime = availability.getDateTimeForBookingSlotDateAndTime(date, idealTime)
        const requiresWheelchairAccess = this.form.controls.requiresWheelchairAccess.value
        const requiresDogFriendly = this.form.controls.requiresDogFriendly.value
        const requiresPramAccessible = this.form.controls.requiresPramAccessible.value
        const requiresHighchairFriendly = this.form.controls.requiresHighchairFriendly.value

        this.searchAvailability.emit({
            partySize,
            date,
            idealTime: selectedIdealTime,
            requiresWheelchairAccess,
            requiresDogFriendly,
            requiresPramAccessible,
            requiresHighchairFriendly,
            ...(this.initialValues?.restrictedReasonIds !== undefined
                ? { restrictedReasonIds: this.initialValues.restrictedReasonIds }
                : {}),
            ...(this.initialValues?.eventId !== undefined
                ? { eventId: this.initialValues.eventId }
                : {}),
            ...(this.initialValues?.areaId !== undefined
                ? { areaId: this.initialValues.areaId }
                : {}),
        })
    }

    protected selectedPartyCannotBeSearched(
        availability: Availability | null = this.availability()
    ): boolean {
        return this.selectedPartyIsLarge(availability)
            && availability !== null
            && !availability.venue.enquiriesEnabled
    }

    private selectedPartyIsLarge(availability: Availability | null): boolean {
        if (availability === null || this.updatingBooking) {
            return false
        }
        const partySize = this.form.controls.partySize.value
        return partySize !== null
            && availability.isLargeParty(
                partySize,
                this.selectedDate,
                this.initialValues?.eventId
            )
    }

    private currentSearchCriteria(): VenueStorefrontBookingSearchInitialValues {
        const searchCriteria: VenueStorefrontBookingSearchInitialValues = {
            ...this.initialValues,
            requiresWheelchairAccess: this.form.controls.requiresWheelchairAccess.value,
            requiresDogFriendly: this.form.controls.requiresDogFriendly.value,
            requiresPramAccessible: this.form.controls.requiresPramAccessible.value,
            requiresHighchairFriendly: this.form.controls.requiresHighchairFriendly.value,
        }
        const partySize = this.form.controls.partySize.value
        const date = this.selectedDate
        const idealTime = this.form.controls.idealTime.value
        if (partySize !== null) {
            searchCriteria.partySize = partySize
        }
        if (date !== null) {
            searchCriteria.date = date
        }
        if (idealTime !== null) {
            searchCriteria.idealTime = idealTime
        }
        return searchCriteria
    }

    private initialiseForVenue() {
        const generation = ++this.loadGeneration
        const initialDate = this.initialValues?.date ?? new Date()
        this.selectedBookingContext.set(this.makeConfiguredBookingContext())
        this.openDaysCalendar = new VenueOpenDaysCalendar()
        this.availability.set(null)
        this.resolvedAvailabilityDate = null
        this.isLoading.set(true)
        this.errorMessage.set(null)
        this.venueNotInService.set(false)
        this.form.reset({
            partySize: this.initialValues?.partySize ?? null,
            date: this.ngbDateAdapter.fromModel(initialDate),
            idealTime: this.initialValues?.idealTime ?? null,
            requiresWheelchairAccess: this.initialValues?.requiresWheelchairAccess ?? false,
            requiresDogFriendly: this.initialValues?.requiresDogFriendly ?? false,
            requiresPramAccessible: this.initialValues?.requiresPramAccessible ?? false,
            requiresHighchairFriendly: this.initialValues?.requiresHighchairFriendly ?? false,
        }, { emitEvent: false })
        if (this.updatingBooking?.isDateRestricted) {
            this.form.controls.date.disable({ emitEvent: false })
            this.form.controls.partySize.disable({ emitEvent: false })
        } else {
            this.form.controls.date.enable({ emitEvent: false })
            this.form.controls.partySize.enable({ emitEvent: false })
        }
        this.visibleDatepickerMonth = this.ngbDateAdapter.fromModel(initialDate)
        this.datepicker?.close()
        if (this.largePartyEnquiry) {
            this.isLoading.set(false)
            return
        }
        this.loadInitialOpenDaysThenAvailability(initialDate, generation)
    }

    private bindAvailabilityRequests() {
        this.availabilityRequests
            .pipe(
                switchMap(request => {
                    this.isLoading.set(true)
                    this.errorMessage.set(null)
                    this.venueNotInService.set(false)
                    return this.getAvailability(request)
                        .pipe(
                            map(availability => ({
                                request,
                                availability,
                                error: null,
                            }) as AvailabilityResult),
                            catchError(error => of({
                                request,
                                availability: null,
                                error,
                            } as AvailabilityResult))
                        )
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
                if (result.request.generation !== this.loadGeneration) {
                    return
                }
                this.isLoading.set(false)
                if (result.error !== null) {
                    this.handleAvailabilityError(result.error)
                    return
                }
                this.handleAvailability(result.availability!, result.request)
            })
    }

    private bindDateChanges() {
        this.form.controls.date.valueChanges
            .pipe(
                filter((date): date is NgbDateStruct => date !== null),
                map(date => ({ date, generation: this.loadGeneration })),
                distinctUntilChanged((first, second) => {
                    return first.generation === second.generation
                        && first.date.year === second.date.year
                        && first.date.month === second.date.month
                        && first.date.day === second.date.day
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(({ date }) => {
                const selectedDate = this.ngbDateAdapter.toModel(date)
                if (!selectedDate) {
                    return
                }
                if (!this.selectedBookingContext()?.event) {
                    this.loadOpenDaysForMonth(selectedDate)
                }
                this.requestAvailability(selectedDate, false)
            })
    }

    private bindPartySizeChanges() {
        this.form.controls.partySize.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.requestLargePartyEnquiryIfNeeded(this.availability())
            })
    }

    private loadInitialOpenDaysThenAvailability(initialDate: Date, generation: number) {
        if (this.selectedBookingContext()?.event) {
            this.requestAvailability(initialDate, false)
            return
        }
        const venue = this.venue
        const openDaysCalendar = this.openDaysCalendar
        const nextMonthDate = new Date(initialDate.getFullYear(), initialDate.getMonth() + 1, 1)
        forkJoin([
            this.getOpenDaysForMonth(initialDate, venue, openDaysCalendar, generation),
            this.getOpenDaysForMonth(nextMonthDate, venue, openDaysCalendar, generation),
        ])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (generation === this.loadGeneration) {
                    this.requestAvailability(initialDate, true)
                }
            })
    }

    private loadOpenDaysForMonth(dateInMonth: Date) {
        this.getOpenDaysForMonth(
            dateInMonth,
            this.venue,
            this.openDaysCalendar,
            this.loadGeneration
        )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe()
    }

    private getOpenDaysForMonth(
        dateInMonth: Date,
        venue: Venue,
        openDaysCalendar: VenueOpenDaysCalendar,
        generation: number
    ): Observable<void> {
        const year = dateInMonth.getFullYear()
        const oneIndexedMonth = dateInMonth.getMonth() + 1
        if (openDaysCalendar.isMonthAvailabilityKnown(year, oneIndexedMonth)) {
            return of(undefined)
        }
        openDaysCalendar.markMonthRequested(year, oneIndexedMonth)
        const startDate = new Date(year, dateInMonth.getMonth(), 1)
        const endDate = new Date(year, dateInMonth.getMonth() + 1, 0)
        return this.availabilityService.getOpenDays(
            venue.organisationId,
            venue.businessId,
            venue.id,
            startDate,
            endDate
        )
            .pipe(
                tap(days => {
                    if (
                        generation !== this.loadGeneration
                        || openDaysCalendar !== this.openDaysCalendar
                    ) {
                        return
                    }
                    openDaysCalendar.addDays(days)
                    this.refreshOpenDatepicker()
                }),
                map(() => undefined),
                catchError(() => {
                    openDaysCalendar.unmarkMonthRequested(year, oneIndexedMonth)
                    return of(undefined)
                })
            )
    }

    private requestAvailability(date: Date, initialLoad: boolean) {
        this.availabilityStatusChange.emit('loading')
        this.availabilityRequests.next({
            date: new Date(date.getFullYear(), date.getMonth(), date.getDate()),
            initialLoad,
            generation: this.loadGeneration,
            venue: this.venue,
            restrictedReasonIds: this.initialValues?.restrictedReasonIds,
        })
    }

    private getAvailability(request: AvailabilityRequest): Observable<Availability> {
        return this.availabilityService.getAvailability(
            request.venue.organisationId,
            request.venue.businessId,
            request.venue.id,
            request.date,
            request.date,
            this.updatingBooking?.bookingId,
            this.updatingBooking?.requiresWheelchairAccess,
            this.updatingBooking?.requiresDogFriendly,
            this.updatingBooking?.requiresPramAccessible,
            this.updatingBooking?.requiresHighchairFriendly,
            request.restrictedReasonIds
        )
    }

    private handleAvailability(availability: Availability, request: AvailabilityRequest) {
        this.availabilityStatusChange.emit('loaded')
        this.availability.set(availability)
        if (this.updatingBooking || !this.selectedBookingContext()) {
            this.selectedBookingContext.set(this.makeConfiguredBookingContext(availability.venue))
        }
        if (this.selectedBookingContext()) {
            this.bookingContextResolved.emit()
        }
        this.setPartySizeDefault(availability)
        this.updateIdealTime(availability, request.date)
        if (request.initialLoad && !this.updatingBooking) {
            this.adjustInitialDateIfNoBookingSlots(availability, request.date)
        }
        this.resolvedAvailabilityDate = request.date
        this.requestLargePartyEnquiryIfNeeded(availability)
    }

    private handleAvailabilityError(error: unknown) {
        this.availabilityStatusChange.emit('failed')
        if (isVenueNotInServiceError(error)) {
            this.venueNotInService.set(true)
            this.errorMessage.set('Online booking is not currently available for this venue.')
            return
        }
        this.errorMessage.set('We could not load availability. Please try again.')
    }

    private setPartySizeDefault(availability: Availability) {
        if (this.form.controls.partySize.value !== null || this.selectedBookingContext()) {
            return
        }
        this.form.controls.partySize.setValue(availability.defaultReservablePartySize())
    }

    private makeConfiguredBookingContext(venue: Venue = this.venue): ConfiguredBookingContext | null {
        const eventId = this.initialValues?.eventId
        const event = eventId ? venue.eventWithId(eventId) : null
        if (event?.notReservableOnline) {
            return null
        }
        const reasonIds = this.initialValues?.restrictedReasonIds
        const reasonId = this.updatingBooking?.reasonId ?? (reasonIds?.length === 1 ? reasonIds[0] : null)
        const reason = reasonId ? venue.reasonWithId(reasonId) : null
        const booking = event ?? reason
        if (!booking) {
            return null
        }
        const description = booking.description?.trim()
        const imageDirectory = event ? 'event-images' : 'reason-images'
        return {
            displayName: booking.displayName,
            event,
            imageUrl: booking.imageId
                ? `${environment.cdnUrl}/582x240/${imageDirectory}/${booking.imageId}`
                : null,
            description: description && richTextPlainText(description)
                ? truncateRichTextHtml(description, 200)
                : null,
            hasCharges: [
                event?.depositAmount,
                event?.depositFlatFeeAmount,
                event?.cancellationChargeAmount,
                event?.cancellationChargePartyAmount,
            ].some(amount => (amount ?? 0) > 0),
        }
    }

    private updateIdealTime(availability: Availability, selectedDate: Date) {
        const currentIdealTime = this.form.controls.idealTime.value
        const event = this.selectedBookingContext()?.event
        if (event) {
            const times = availability.bookingSlotTimesForEvent(event.id)
            if (times.length === 0) {
                this.form.controls.idealTime.setValue(null)
                return
            }
            const preferredTime = currentIdealTime
                ?? event.recurrence.startTime?.dateTimeOnDate(selectedDate)
                ?? times[0]
            const preferredTimeOnSelectedDate = new Date(selectedDate)
            preferredTimeOnSelectedDate.setHours(
                preferredTime.getHours(),
                preferredTime.getMinutes(),
                0,
                0
            )
            const closestTime = [...times].sort((first, second) => {
                return Math.abs(first.getTime() - preferredTimeOnSelectedDate.getTime())
                    - Math.abs(second.getTime() - preferredTimeOnSelectedDate.getTime())
            })[0]
            this.form.controls.idealTime.setValue(closestTime)
            return
        }
        if (currentIdealTime === null) {
            this.form.controls.idealTime.setValue(
                availability.defaultIdealTime(selectedDate, new Date())
            )
            return
        }

        const idealTimeOnSelectedDate = new Date(selectedDate)
        idealTimeOnSelectedDate.setHours(
            currentIdealTime.getHours(),
            currentIdealTime.getMinutes(),
            0,
            0
        )
        const closestTime = availability.closestIntervalIdealTime(currentIdealTime, selectedDate)
        this.form.controls.idealTime.setValue(closestTime ?? idealTimeOnSelectedDate)
    }

    private requestLargePartyEnquiryIfNeeded(availability: Availability | null, explicitRequest = false) {
        const partySize = this.form.controls.partySize.value
        const date = this.selectedDate
        const idealTime = this.form.controls.idealTime.value
        if (
            availability === null
            || partySize === null
            || date === null
            || idealTime === null
            || this.largePartyEnquiry
            || (!explicitRequest && this.initialValues?.autoOpen === false && this.form.pristine)
            || !availability.venue.enquiriesEnabled
            || !this.sameDay(date, this.resolvedAvailabilityDate)
            || !availability.isLargeParty(partySize, date, this.initialValues?.eventId)
        ) {
            return
        }
        this.largePartyEnquiryRequested.emit(this.currentSearchCriteria())
    }

    private sameDay(first: Date, second: Date | null): boolean {
        return second !== null
            && first.getFullYear() === second.getFullYear()
            && first.getMonth() === second.getMonth()
            && first.getDate() === second.getDate()
    }

    private adjustInitialDateIfNoBookingSlots(availability: Availability, selectedDate: Date) {
        const earliestBookingSlot = availability.earliestAvailability()
        if (earliestBookingSlot && earliestBookingSlot.options.length > 0) {
            return
        }
        const today = new Date()
        const selectedDay = new Date(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate()
        )
        const currentDay = new Date(today.getFullYear(), today.getMonth(), today.getDate())
        if (selectedDay.getTime() !== currentDay.getTime()) {
            return
        }
        const nextDate = this.openDaysCalendar.firstOpenDateAfter(today) ?? new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() + 1
        )
        this.form.controls.date.setValue(this.ngbDateAdapter.fromModel(nextDate))
    }

    private refreshOpenDatepicker() {
        if (!this.datepicker?.isOpen()) {
            return
        }
        const visibleMonth = this.visibleDatepickerMonth
        this.datepicker.close()
        this.datepicker.open()
        if (visibleMonth) {
            this.datepicker.navigateTo(visibleMonth)
        }
    }
}
